import { useState } from 'react';
import { Box, Card, Link, Text, Title, Toggle } from '@nimbus-ds/components';
import {
  CheckCircleIcon,
  CloseIcon,
  CreditCardIcon,
  EditIcon,
  HomeIcon,
  LockIcon,
  ShoppingCartIcon,
  StoreIcon,
  TruckIcon,
  UserCircleIcon,
} from '@nimbus-ds/icons';
import {
  NotificationDto,
  NotificationsListItemResponseDto,
} from '@tiendanube/common';
import { InvoiceIcon } from '@tiendanube/icons';
import { useNavegate } from 'App/hooks';
import { IconBox } from 'commons/components';
import { useToast } from 'commons/hooks';
import {
  usePartialUpdateEmailMutation,
  useUpdateWhatsappNotificationByIdMutation,
} from 'domains/Configurations/Notifications/notificationsApi';
import { trackingEmailNotificationEnabledClick } from 'domains/Configurations/tracking';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';

const emailIcons = {
  abandonedcheckoutrecover: <ShoppingCartIcon />,
  customer_activate_account: <UserCircleIcon />,
  customer_reset_password: <LockIcon />,
  customer_welcome_account: <HomeIcon />,
  ordercancelled: <CloseIcon />,
  ordercaptured: <CreditCardIcon />,
  orderconfirmation: <CheckCircleIcon />,
  ordershipped: <TruckIcon />,
  orderedited: <EditIcon />,
  orderreadyforpickup: <StoreIcon />,
  orderinvoice: <InvoiceIcon />,
};

interface EmailItemCardProps {
  email: NotificationsListItemResponseDto;
  whatsappNotification?: NotificationDto;
}

function EmailItemCard({
  email,
  whatsappNotification,
}: Readonly<EmailItemCardProps>) {
  const t = useTranslationConfigurations('notifications.notificationsListPage');
  const { goTo } = useNavegate();
  const handleClickEdit = () => goTo(`/settings/notifications/${email.id}`);
  const [emailEnabled, setEmailEnabled] = useState(email.active);
  const isOrderPickupEmail = email.type === 'orderreadyforpickup';
  const { addToast } = useToast();

  const [updateEmail] = usePartialUpdateEmailMutation();
  const [updateWhatsappNotification] =
    useUpdateWhatsappNotificationByIdMutation();

  const handleEmailEnabledChanged = () => {
    const newState = !emailEnabled;

    updateEmail({
      id: email.id.toString(),
      payload: {
        data: {
          active: newState,
        },
      },
    })
      .unwrap()
      .then(
        () => {
          setEmailEnabled(newState);
          trackingEmailNotificationEnabledClick(email.type, newState);

          if (whatsappNotification?.whatsapp?.available) {
            updateWhatsappNotification({
              id: whatsappNotification.id,
              payload: {
                whatsapp: {
                  available: whatsappNotification.whatsapp.available,
                  enabled: newState,
                },
              },
            }).catch(() => {
              // Silent failure
            });
          }
        },
        () => {
          addToast({
            appearance: 'danger',
            label: t('toggleEmail.error'),
          });
        },
      );
  };

  return (
    <div className="stratus--email-item-card">
      <Card padding="small">
        <Card.Body>
          <Box
            display="flex"
            gap="2"
            alignItems="flex-start"
            justifyContent="flex-start"
          >
            <IconBox
              color="primary-interactive"
              backgroundColor="primary-surface"
              height="32px"
              width="32px"
              minWidth="32px"
            >
              {emailIcons[email.type]}
            </IconBox>
            <Box display="flex" flexDirection="column" gap="2" flex="1">
              <Box
                display="flex"
                justifyContent="space-between"
                flexDirection="row"
                width="100%"
              >
                <Box
                  display="flex"
                  flexDirection="row"
                  gap="2"
                  alignItems="center"
                >
                  <Title as="h5" lineHeight="8">
                    {email.title}
                  </Title>
                </Box>
                {isOrderPickupEmail && (
                  <Toggle
                    name="orderpickupemailtoggle"
                    checked={emailEnabled}
                    onChange={handleEmailEnabledChanged}
                  />
                )}
              </Box>
              <Text>{email.description}</Text>
              <Link
                as="a"
                onClick={handleClickEdit}
                appearance="primary"
                textDecoration="none"
              >
                {t('editContent')}
              </Link>
            </Box>
          </Box>
        </Card.Body>
      </Card>
    </div>
  );
}

export default EmailItemCard;
